/**
 * ResilientEventSource - SSE connection with exponential backoff
 *
 * Provides robust reconnection handling for Server-Sent Events with:
 * - Exponential backoff with jitter to prevent thundering herd
 * - Configurable max retries and timeouts
 * - Connection state tracking for UI feedback
 * - Stale connection detection (zombie connection handling)
 * - Graceful degradation on persistent failures
 */

export type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'failed' | 'stale';

export interface ResilientEventSourceOptions {
  /** Initial delay before first retry (ms). Default: 1000 */
  initialRetryDelay?: number;
  /** Maximum delay between retries (ms). Default: 30000 */
  maxRetryDelay?: number;
  /** Multiplier for exponential backoff. Default: 2 */
  backoffMultiplier?: number;
  /** Maximum number of retry attempts. Default: 10. Set to Infinity for unlimited. */
  maxRetries?: number;
  /** Add random jitter to prevent thundering herd (0-1). Default: 0.3 */
  jitterFactor?: number;
  /** Time without messages before considering connection stale (ms). Default: 60000 (1 min) */
  staleTimeout?: number;
  /** How often to check for stale connections (ms). Default: 15000 */
  staleCheckInterval?: number;
  /** Callback when connection state changes */
  onStateChange?: (state: ConnectionState, attempt?: number) => void;
  /** Callback when max retries exceeded */
  onMaxRetriesExceeded?: () => void;
  /** Callback when connection detected as stale */
  onStaleConnection?: () => void;
}

const DEFAULT_OPTIONS: Required<Omit<ResilientEventSourceOptions, 'onStateChange' | 'onMaxRetriesExceeded' | 'onStaleConnection'>> = {
  initialRetryDelay: 1000,
  maxRetryDelay: 30000,
  backoffMultiplier: 2,
  maxRetries: 10,
  jitterFactor: 0.3,
  staleTimeout: 60000, // 1 minute without data = stale
  staleCheckInterval: 15000, // Check every 15 seconds
};

export class ResilientEventSource {
  private url: string;
  private options: Required<Omit<ResilientEventSourceOptions, 'onStateChange' | 'onMaxRetriesExceeded' | 'onStaleConnection'>> &
    Pick<ResilientEventSourceOptions, 'onStateChange' | 'onMaxRetriesExceeded' | 'onStaleConnection'>;
  private eventSource: EventSource | null = null;
  private retryCount = 0;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private currentState: ConnectionState = 'disconnected';
  private isManuallyClosed = false;
  private messageListeners: Map<string, Set<(event: MessageEvent) => void>> = new Map();
  private errorListeners: Set<(event: Event) => void> = new Set();
  private openListeners: Set<() => void> = new Set();

  // Stale connection detection
  private lastMessageTime: number = 0;
  private staleCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  private isStreamEnded = false; // Track if stream ended gracefully (no need to check for stale)

  constructor(url: string, options: ResilientEventSourceOptions = {}) {
    this.url = url;
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Record that we received a message (resets stale timer)
   */
  private recordMessageReceived(): void {
    this.lastMessageTime = Date.now();
  }

  /**
   * Start monitoring for stale connections
   */
  private startStaleDetection(): void {
    this.stopStaleDetection(); // Clear any existing interval

    this.staleCheckIntervalId = setInterval(() => {
      // Don't check if stream ended gracefully or manually closed
      if (this.isStreamEnded || this.isManuallyClosed) {
        return;
      }

      // Don't check if not connected
      if (this.currentState !== 'connected') {
        return;
      }

      const timeSinceLastMessage = Date.now() - this.lastMessageTime;

      if (timeSinceLastMessage > this.options.staleTimeout) {
        console.warn(
          `[ResilientEventSource] Connection stale - no data for ${Math.round(timeSinceLastMessage / 1000)}s. ` +
          `Triggering reconnect...`
        );

        this.setState('stale');
        this.options.onStaleConnection?.();

        // Force reconnection
        this.handleStaleConnection();
      }
    }, this.options.staleCheckInterval);
  }

  /**
   * Stop stale connection monitoring
   */
  private stopStaleDetection(): void {
    if (this.staleCheckIntervalId) {
      clearInterval(this.staleCheckIntervalId);
      this.staleCheckIntervalId = null;
    }
  }

  /**
   * Handle a stale connection by forcing reconnection
   */
  private handleStaleConnection(): void {
    // Close current connection
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    // Reset retry count for fresh start after stale detection
    this.retryCount = 0;

    // Reconnect immediately (stale != error, so we don't need backoff)
    this.connect();
  }

  /**
   * Mark the stream as gracefully ended (no more stale checks needed)
   */
  markStreamEnded(): void {
    this.isStreamEnded = true;
    this.stopStaleDetection();
  }

  /**
   * Calculate delay with exponential backoff and jitter
   */
  private calculateRetryDelay(): number {
    const { initialRetryDelay, maxRetryDelay, backoffMultiplier, jitterFactor } = this.options;

    // Exponential backoff: delay = initial * multiplier^retryCount
    const exponentialDelay = initialRetryDelay * Math.pow(backoffMultiplier, this.retryCount);

    // Cap at max delay
    const cappedDelay = Math.min(exponentialDelay, maxRetryDelay);

    // Add jitter to prevent thundering herd
    const jitter = cappedDelay * jitterFactor * Math.random();

    return Math.floor(cappedDelay + jitter);
  }

  /**
   * Update and broadcast connection state
   */
  private setState(state: ConnectionState): void {
    if (this.currentState !== state) {
      this.currentState = state;
      this.options.onStateChange?.(state, this.retryCount);
    }
  }

  /**
   * Connect to the SSE endpoint
   */
  connect(): void {
    if (this.eventSource) {
      this.eventSource.close();
    }

    this.isManuallyClosed = false;
    this.isStreamEnded = false; // Reset stream ended flag on new connection
    this.setState(this.retryCount === 0 ? 'connecting' : 'reconnecting');

    try {
      this.eventSource = new EventSource(this.url);

      this.eventSource.onopen = () => {
        this.retryCount = 0; // Reset retry count on successful connection
        this.recordMessageReceived(); // Initialize last message time
        this.startStaleDetection(); // Start monitoring for stale connections
        this.setState('connected');
        this.openListeners.forEach(listener => listener());
      };

      this.eventSource.onerror = (event) => {
        // Don't retry if manually closed
        if (this.isManuallyClosed) {
          return;
        }

        // Notify error listeners
        this.errorListeners.forEach(listener => listener(event));

        // Close current connection
        if (this.eventSource) {
          this.eventSource.close();
          this.eventSource = null;
        }

        // Check if we should retry
        if (this.retryCount >= this.options.maxRetries) {
          this.setState('failed');
          this.options.onMaxRetriesExceeded?.();
          return;
        }

        // Schedule retry with backoff
        this.scheduleRetry();
      };

      // Re-attach all message listeners
      this.messageListeners.forEach((listeners, eventType) => {
        listeners.forEach(listener => {
          this.eventSource?.addEventListener(eventType, listener as EventListener);
        });
      });

    } catch (error) {
      console.error('[ResilientEventSource] Failed to create EventSource:', error);
      this.scheduleRetry();
    }
  }

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  private scheduleRetry(): void {
    if (this.isManuallyClosed) return;

    const delay = this.calculateRetryDelay();
    this.retryCount++;

    this.setState('reconnecting');

    this.retryTimeoutId = setTimeout(() => {
      if (!this.isManuallyClosed) {
        this.connect();
      }
    }, delay);
  }

  /**
   * Add an event listener for SSE messages
   */
  addEventListener(type: string, listener: (event: MessageEvent) => void): void {
    // Wrap listener to record message time (for stale detection)
    const wrappedListener = (event: MessageEvent) => {
      this.recordMessageReceived();
      listener(event);
    };

    if (!this.messageListeners.has(type)) {
      this.messageListeners.set(type, new Set());
    }
    this.messageListeners.get(type)!.add(wrappedListener);

    // If already connected, attach immediately
    if (this.eventSource) {
      this.eventSource.addEventListener(type, wrappedListener as EventListener);
    }
  }

  /**
   * Remove an event listener
   */
  removeEventListener(type: string, listener: (event: MessageEvent) => void): void {
    this.messageListeners.get(type)?.delete(listener);
    this.eventSource?.removeEventListener(type, listener as EventListener);
  }

  /**
   * Add error listener
   */
  onError(listener: (event: Event) => void): void {
    this.errorListeners.add(listener);
  }

  /**
   * Add open/connect listener
   */
  onOpen(listener: () => void): void {
    this.openListeners.add(listener);
  }

  /**
   * Close the connection and stop retrying
   */
  close(): void {
    this.isManuallyClosed = true;

    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    this.stopStaleDetection();

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.setState('disconnected');
  }

  /**
   * Force a retry with backoff in response to an in-band server-signalled
   * error (e.g. an SSE `event: 'error'` message). Mirrors the private
   * onerror path so retry count, backoff, and maxRetries are all honored.
   */
  triggerRetry(): void {
    if (this.isManuallyClosed) return;

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    if (this.retryCount >= this.options.maxRetries) {
      this.setState('failed');
      this.options.onMaxRetriesExceeded?.();
      return;
    }

    this.scheduleRetry();
  }

  /**
   * Reset retry count and reconnect (useful for manual refresh)
   */
  reconnect(): void {
    this.retryCount = 0;
    this.isStreamEnded = false;
    this.stopStaleDetection();

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    // Don't use close() here as it sets isManuallyClosed = true
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    this.isManuallyClosed = false;
    this.connect();
  }

  /**
   * Get time since last message (for debugging/UI)
   */
  getTimeSinceLastMessage(): number {
    if (this.lastMessageTime === 0) return 0;
    return Date.now() - this.lastMessageTime;
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.currentState;
  }

  /**
   * Get current retry count
   */
  getRetryCount(): number {
    return this.retryCount;
  }
}
